"use client";

import { useCallback, useEffect, useState } from "react";

import Link from "next/link";
import { useRouter } from "next/navigation";

import { ChevronLeftIcon } from "@/components/icons/chevronLeft.icon";

import {
  QUIZ_RESULTS_PAGE,
  STOREFRONT_HOME_PAGE,
} from "@/constants/page.constants";

import { useQuizFlow } from "../hooks/useQuizFlow";
import { isQuizCompleted } from "../utils/quizStorage";
import { QuizData } from "./quizFlow.types";
import { QuizFlowLoader } from "./quizFlowLoader";
import { QuizModal } from "./quizModal/quizModal.component";
import { QuizQuestion } from "./quizQuestion.component";
import { QuizStepper } from "./quizStepper.component";
import { RetakeModalContent } from "./retakeModal/retakeModal.component";

export const QuizFlow = ({ quiz, startQuestionKey, source }: QuizFlowProps) => {
  const router = useRouter();
  const [isRetakeOpen, setIsRetakeOpen] = useState(false);
  const onFinish = useCallback(() => router.push(QUIZ_RESULTS_PAGE), [router]);

  const {
    quizId,
    currentQuestion,
    currentStepIndex,
    isLoading,
    isRehydrated,
    answers,
    stepLabels,
    finishStepIndex,
    canGoBack,
    handleSingleChoiceSelect,
    handleMultipleChoiceNext,
    handleNumberInputNext,
    handleLeadCaptureSubmit,
    handleLeadCaptureSkip,
    handleBack,
    resetQuiz,
  } = useQuizFlow({ quiz, startQuestionKey, source, onFinish });

  const retakeModal = quiz.retakeModal;

  useEffect(() => {
    if (isQuizCompleted(quizId)) {
      setIsRetakeOpen(true);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [currentQuestion?.questionKey]);

  useEffect(() => {
    if (isLoading) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [isLoading]);

  if (!isRehydrated) return <QuizFlowLoader quiz={quiz} />;
  if (!currentQuestion && !isLoading) return null;

  const activeStepIndex = isLoading ? finishStepIndex : currentStepIndex;

  const renderContent = () => {
    if (isLoading) return <QuizFlowLoader quiz={quiz} />;
    if (!currentQuestion) return null;

    return (
      <QuizQuestion
        key={currentQuestion.questionKey}
        question={currentQuestion}
        onSingleChoiceSelect={handleSingleChoiceSelect}
        onMultipleChoiceNext={handleMultipleChoiceNext}
        onNumberInputNext={handleNumberInputNext}
        onLeadCaptureSubmit={handleLeadCaptureSubmit}
        onLeadCaptureSkip={handleLeadCaptureSkip}
        initialAnswer={answers[currentQuestion.questionKey]}
      />
    );
  };

  return (
    <div
      data-testid="quiz-page"
      className="flex min-h-[60vh] flex-col px-5 md:px-0">
      <nav
        data-testid="quiz-breadcrumb"
        className="container mx-auto hidden px-10 md:block">
        <div className="flex items-center gap-2 text-sm">
          <Link
            href={STOREFRONT_HOME_PAGE}
            className="text-greyscale-strength hover:text-secondary">
            Home
          </Link>
          <span className="text-greyscale-strength">/</span>
          <span className="text-greyscale-body">Quiz</span>
        </div>
      </nav>

      <div className="container mx-auto mt-6 flex flex-col-reverse gap-6 md:flex-col md:px-24">
        <QuizStepper steps={stepLabels} activeStepIndex={activeStepIndex} />
        <button
          data-testid="quiz-back-button"
          onClick={handleBack}
          disabled={!canGoBack || isLoading}
          className="text-secondary flex items-center gap-1 text-sm underline underline-offset-2 disabled:invisible">
          <ChevronLeftIcon width={16} height={16} />
          Back
        </button>
      </div>

      <div className="container mx-auto mt-16 mb-34 px-4 md:px-8">
        {renderContent()}
      </div>

      {retakeModal && (
        <QuizModal open={isRetakeOpen} onClose={() => setIsRetakeOpen(false)}>
          <RetakeModalContent
            data={retakeModal}
            onRetake={resetQuiz}
            onClose={() => setIsRetakeOpen(false)}
          />
        </QuizModal>
      )}
    </div>
  );
};

type QuizFlowProps = {
  quiz: QuizData;
  startQuestionKey?: string;
  source?: string;
};
